import { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import DeductionsTableDataRow from './DeductionsTableDataRow';

import { getDeductionsList } from '../../../api/paymentsApi/deductionsApi';
import Loader from '../../../components/Loader';
import NoDataText from '../../../components/NoDataText';
import TableContainer from '../../../components/Table/TableContainer';
import { DEDUCTIONS_LIST_TABLE_LABELS } from '../../../constants/tableLabels';
import usePagination from '../../../hooks/usePagination';
import { ActiveSortField, DeductionData, FilterTypes } from '../../../types';
import TableFilters from '../../../components/TableFilters/TableFilters';
import { downloadCsvorExcel } from '../../../helpers/downloadCsv';
import TableActions from '../../../components/Table/TableActions';
import {
  DEDUCTION_TYPE_OPTIONS,
  MAPPED_DEDUCTIONS_STATUS_FILTER_OPTIONS,
} from '../../../components/TableFilters/constants';
import useSnackBar from '../../../hooks/useSnackBar';
import { handleSortTableData } from '../../../helpers/handleSortTableData';
import { useHasPermission } from '../../../hooks/useHasPermission';

const DeductionsView = () => {
  const params = useParams();
  const firmId = localStorage.getItem('firmId');

  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();
  const canCreate = useHasPermission('Payments', 'Create');

  const [deductionsList, setDeductionsList] = useState<DeductionData[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showFilters, setShowFilters] = useState<boolean>(true);

  const [filters, setFilters] = useState<FilterTypes>({
    firmId: firmId || '',
    clientId: '',
    defendantId: '',
    vendorId: '',
  });
  const [match, setMatch] = useState<string>('');
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  // Bumped after a recovery is saved so the list is fetched again
  const [refreshKey, setRefreshKey] = useState(0);
  const sortableFields = {
    caseName: {
      order: '',
      fieldName: 'caseName',
    },
    createdAt: {
      order: '',
      fieldName: 'createdAt',
    },
    paidAt: {
      order: '',
      fieldName: 'paidAt',
    },
     updatedAt: {
      order: '',
      fieldName: 'updatedAt',
    },
  };

  const hasParams = !!Object.entries(params).length;

  useEffect(() => {
    getDeductionsList({
      page: pageNumber,
      size: rowsPerPage,
      count: pageNumber === 1,
      match: match,
      filters: { ...filters, ...(hasParams && params) },
      activeSortField,
    }).then((res) => {
      if (res.data) {
        pageNumber === 1 && setTotalCount?.(res.data.totalCount || 0);
        setDeductionsList(res.data.resultSet);
        setIsDataLoading(false);
      }
    });
  }, [pageNumber, rowsPerPage, filters, match, activeSortField, refreshKey]);

  const exportList = () => {
    downloadCsvorExcel('/v2/deductions/export', 'POST', 'Deductions_List','csv', {
      page: pageNumber,
      size: rowsPerPage,
      count: true,
      match: match,
      filters: { ...filters, ...(hasParams && params) },
    }).then((res) => {
      if (res?.data) {
        setAlert({
          message: 'Deductions exported successfully.',
          type: 'success',
        });
      }
    });
  };

  return (
    <>
      {isDataLoading && !deductionsList.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <TableActions
            hasParams={hasParams}
            createLink={canCreate ? `/payments/create-deduction${
              params.caseId ? `/?caseId=${params.caseId}` : '/'
            }` : undefined}
            exportList={exportList}
            match={match}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            setMatch={setMatch}
          />

          {showFilters && (
            <TableFilters
              filters={filters}
              setFilters={setFilters}
              setMatch={setMatch}
              client
              // defendant
              timeFilter={'Entry Date'}
              typeFilter
              typeFilterOptions={DEDUCTION_TYPE_OPTIONS}
              mappedDeductionStatus={MAPPED_DEDUCTIONS_STATUS_FILTER_OPTIONS}
              isPlaintiff
              vendor
            />
          )}

          {!isDataLoading && deductionsList.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={DEDUCTIONS_LIST_TABLE_LABELS}
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
            >
              <DeductionsTableDataRow
                tableData={deductionsList}
                onRecoverySaved={() => setRefreshKey((key) => key + 1)}
              />
            </TableContainer>
          )}
        </>
      )}
      <Outlet />
    </>
  );
};

export default DeductionsView;
